// Interactive Google map with a draggable pin for a location.
//
// The Maps JavaScript API key comes from the org's google_maps integration
// (GET /settings/google-maps/key). The script is injected once per page and
// shared across every LocationMap instance via a module-level promise.
//
// Props:
//   lat, lon      — current pin coordinates (numbers) or null/undefined
//   onPinChange   — (lat, lon) => void, fired after the operator drags the pin
//   height        — map height in px (default 280)
//   interactive   — when false, pin isn't draggable (view-only)

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

let mapsPromise = null;

// Load the Maps JS API exactly once. Resolves with the global `google` object.
function loadGoogleMaps(key) {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => { mapsPromise = null; reject(new Error('Failed to load Google Maps')); };
    document.head.appendChild(s);
  });
  return mapsPromise;
}

const DEFAULT_CENTER = { lat: 40.2247, lng: -105.2717 }; // Lyons, CO fallback

export default function LocationMap({ lat, lon, onPinChange, height = 280, interactive = true }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  // The browser Maps key — 404 means the integration isn't configured.
  const keyQ = useQuery({
    queryKey: ['google-maps-key'],
    queryFn: () => api.get('/settings/google-maps/key'),
    retry: false,
    staleTime: 60 * 60 * 1000,
  });
  const key = keyQ.data?.key;

  const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);

  // Init the map once the key + container are ready.
  useEffect(() => {
    if (!key || !divRef.current) return;
    let cancelled = false;
    loadGoogleMaps(key).then((google) => {
      if (cancelled || !divRef.current) return;
      const center = hasCoords ? { lat, lng: lon } : DEFAULT_CENTER;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(divRef.current, {
          center, zoom: hasCoords ? 17 : 12, mapTypeControl: false, streetViewControl: false,
        });
      }
      if (!markerRef.current) {
        markerRef.current = new google.maps.Marker({
          map: mapRef.current, draggable: interactive,
        });
        if (interactive) {
          markerRef.current.addListener('dragend', (e) => {
            onPinChange?.(e.latLng.lat(), e.latLng.lng());
          });
        }
      }
      if (hasCoords) {
        const pos = { lat, lng: lon };
        markerRef.current.setPosition(pos);
        markerRef.current.setMap(mapRef.current);
        mapRef.current.setCenter(pos);
      } else {
        markerRef.current.setMap(null);
      }
    }).catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [key, lat, lon, hasCoords, interactive, onPinChange]);

  if (keyQ.isError) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-4 text-sm text-amber-800">
        Google Maps isn&rsquo;t configured. Add a key in{' '}
        <a href="/settings?tab=integrations" className="font-medium underline">Settings → Integrations</a>{' '}
        to see the map.
      </div>
    );
  }
  if (loadError) {
    return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-4 text-sm text-red-700">{loadError}</div>;
  }

  return (
    <div className="relative">
      <div ref={divRef} style={{ height, width: '100%' }} className="overflow-hidden rounded-md border border-gray-200 bg-gray-100" />
      {!hasCoords && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-md bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-600 shadow">
            Not geocoded yet — use “Validate &amp; geocode”.
          </span>
        </div>
      )}
      {interactive && hasCoords && (
        <p className="mt-1 text-xs text-gray-400">Drag the pin to fine-tune the exact location.</p>
      )}
    </div>
  );
}
